import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Printer, ChevronLeft, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const LOGO = "https://content.webfactorysite.co.uk/20260113092111-Wales-Event-Logo.png?t=1768296071";
const ROLE_LABELS = { admin: "Admin", senior_medic: "Senior Medic", staff: "Staff" };
const OUTCOME_LABELS = {
  returned_to_event: "Returned to event",
  left_scene: "Left scene (own accord)",
  taken_to_hospital: "Taken to hospital",
  refused_care: "Refused care / self-discharged",
  other: "Other",
};

function fmt(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  return `${d.toLocaleDateString("en-GB")} ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
}

export default function PatientReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [observations, setObservations] = useState([]);
  const [notes, setNotes] = useState([]);
  const [medications, setMedications] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, o, n, m, s] = await Promise.all([
          api.get(`/patients/${id}`),
          api.get(`/patients/${id}/observations`),
          api.get(`/patients/${id}/notes`),
          api.get(`/patients/${id}/medications`),
          api.get(`/patients/${id}/signatures`),
        ]);
        setPatient(p.data);
        setObservations(o.data);
        setNotes(n.data);
        setMedications(m.data);
        setSignatures(s.data);
      } catch (e) {
        toast.error(formatApiError(e));
        navigate(`/patients/${id}`);
      }
    })();
  }, [id, navigate]);

  useEffect(() => {
    if (patient) document.title = `Patient Clinical Report - ${patient.first_name} ${patient.last_name}`;
    return () => { document.title = "Wales Event Medical — Staff Portal"; };
  }, [patient]);

  if (!patient) return <div className="min-h-screen grid place-items-center text-slate-400 text-sm">Preparing report…</div>;

  const sendEmail = async () => {
    if (!emailTo.trim()) return toast.error("Enter the patient's email address");
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/patients/${id}/report/email`, { to_email: emailTo.trim() });
      toast.success(data.message);
      setEmailOpen(false);
      setEmailTo("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-200 print:bg-white">
      <div className="no-print sticky top-0 z-40 bg-[#0F2547] text-white px-4 sm:px-8 py-3 flex justify-between items-center">
        <button onClick={() => navigate(`/patients/${id}`)} data-testid="back-to-patient-btn"
          className="inline-flex items-center gap-1 text-sm font-semibold hover:text-sky-300 transition-colors min-h-[44px]">
          <ChevronLeft className="h-4 w-4" /> Back to patient
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => { setEmailTo(patient.email || ""); setEmailOpen(true); }} data-testid="email-report-button"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 transition-colors text-sm font-semibold px-5 py-2.5 rounded-lg min-h-[44px]">
            <Mail className="h-4 w-4" /> Email to patient
          </button>
          <button onClick={() => window.print()} data-testid="print-report-button"
            className="inline-flex items-center gap-2 bg-[#005EB8] hover:bg-[#004a93] transition-colors text-sm font-semibold px-5 py-2.5 rounded-lg min-h-[44px]">
            <Printer className="h-4 w-4" /> Print / Save as PDF
          </button>
        </div>
      </div>

      <div className="print-page max-w-4xl mx-auto bg-white my-6 print:my-0 p-8 sm:p-12 shadow-lg print:shadow-none" data-testid="patient-report">
        <div className="flex items-start justify-between border-b-4 border-[#005EB8] pb-6 mb-6">
          <div>
            <img src={LOGO} alt="Wales Event Medical Services" className="h-14 w-auto mb-3" />
            <h1 className="font-heading text-2xl font-extrabold text-[#0F2547]">Patient Clinical Report</h1>
            <p className="text-xs text-slate-500 mt-1">Generated {fmt(new Date().toISOString())} · Confidential medical record</p>
          </div>
          <div className="text-right text-xs text-slate-500">
            <p className="font-bold text-[#0F2547]">Wales Event Medical Services</p>
            <p>info@waleseventmedical.co.uk</p>
          </div>
        </div>

        <section className="mb-8">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8] mb-3">Patient Details</h2>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <p><span className="font-semibold text-slate-500">Name:</span> {patient.first_name} {patient.middle_names} {patient.last_name}</p>
            <p><span className="font-semibold text-slate-500">Date of birth:</span> {patient.dob || "—"}</p>
            {patient.arrived_at && <p><span className="font-semibold text-slate-500">Arrived:</span> {fmt(patient.arrived_at)}</p>}
            <p><span className="font-semibold text-slate-500">Telephone:</span> {patient.phone || "—"}</p>
            <p><span className="font-semibold text-slate-500">NHS number:</span> {patient.nhs_number || "—"}</p>
            <p className="col-span-2"><span className="font-semibold text-slate-500">Address:</span> {patient.address || "—"}</p>
            <p><span className="font-semibold text-slate-500">Event / location:</span> {patient.event_location || "—"}</p>
            <p><span className="font-semibold text-slate-500">Emergency contact:</span> {patient.emergency_contact_name || "—"} {patient.emergency_contact_phone && `(${patient.emergency_contact_phone})`}</p>
            {patient.injuries?.length > 0 && (
              <p className="col-span-2"><span className="font-semibold text-slate-500">Injury sites:</span> {patient.injuries.join(", ")}</p>
            )}
            {patient.status === "discharged" && (
              <p className="col-span-2"><span className="font-semibold text-slate-500">Disposition:</span> {OUTCOME_LABELS[patient.discharge_outcome] || "Discharged"}{patient.discharged_at && ` · ${fmt(patient.discharged_at)}`}</p>
            )}
          </div>
          {patient.allergies?.length > 0 && (
            <div className="mt-4 border-2 border-[#D5281B] bg-red-50 rounded-lg px-4 py-3">
              <p className="font-bold text-[#D5281B] text-sm">ALLERGIES: {patient.allergies.join(", ")}</p>
            </div>
          )}
        </section>

        <section className="mb-8">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8] mb-3">Observations ({observations.length})</h2>
          {observations.length === 0 ? (
            <p className="text-sm text-slate-500">No observations recorded.</p>
          ) : (
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-[#0F2547] text-white">
                  <th className="text-left px-2 py-2">Time</th>
                  <th className="px-2 py-2">HR</th>
                  <th className="px-2 py-2">BP</th>
                  <th className="px-2 py-2">RR</th>
                  <th className="px-2 py-2">SpO2</th>
                  <th className="px-2 py-2">Temp</th>
                  <th className="px-2 py-2">BM</th>
                  <th className="px-2 py-2">Pupils</th>
                  <th className="px-2 py-2">AVPU</th>
                  <th className="px-2 py-2">NEWS2</th>
                  <th className="text-left px-2 py-2">Recorded by</th>
                </tr>
              </thead>
              <tbody>
                {[...observations].reverse().map((o, i) => (
                  <tr key={o.id} className={i % 2 ? "bg-slate-50" : ""}>
                    <td className="px-2 py-2 font-semibold whitespace-nowrap">{fmt(o.created_at)}</td>
                    <td className="text-center px-2 py-2">{o.heart_rate ?? "—"}</td>
                    <td className="text-center px-2 py-2">{o.bp_systolic != null ? `${o.bp_systolic}/${o.bp_diastolic ?? "—"}` : "—"}</td>
                    <td className="text-center px-2 py-2">{o.respiratory_rate ?? "—"}</td>
                    <td className="text-center px-2 py-2">{o.spo2 != null ? `${o.spo2}%` : "—"}</td>
                    <td className="text-center px-2 py-2">{o.temperature != null ? `${o.temperature}°C` : "—"}</td>
                    <td className="text-center px-2 py-2">{o.blood_glucose ?? "—"}</td>
                    <td className="text-center px-2 py-2">{[o.pupils, o.pupil_left_mm != null || o.pupil_right_mm != null ? `L${o.pupil_left_mm ?? "—"}/R${o.pupil_right_mm ?? "—"}mm` : null].filter(Boolean).join(" ") || "—"}</td>
                    <td className="text-center px-2 py-2">{o.consciousness || "—"}</td>
                    <td className="text-center px-2 py-2 font-bold">{o.news2}</td>
                    <td className="px-2 py-2">{o.recorded_by_name} ({ROLE_LABELS[o.recorded_by_role] || o.recorded_by_role})</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="mb-8">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8] mb-3">Medications Given ({medications.length})</h2>
          {medications.length === 0 ? (
            <p className="text-sm text-slate-500">No medications recorded.</p>
          ) : (
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-[#0F2547] text-white">
                  <th className="text-left px-2 py-2">Time</th>
                  <th className="text-left px-2 py-2">Medication</th>
                  <th className="text-left px-2 py-2">Dosage</th>
                  <th className="text-left px-2 py-2">Batch no.</th>
                  <th className="text-left px-2 py-2">Expiry</th>
                  <th className="text-left px-2 py-2">Administered by</th>
                </tr>
              </thead>
              <tbody>
                {[...medications].reverse().map((m, i) => (
                  <tr key={m.id} className={i % 2 ? "bg-slate-50" : ""}>
                    <td className="px-2 py-2 whitespace-nowrap font-semibold">{fmt(m.created_at)}</td>
                    <td className="px-2 py-2 font-semibold">{m.medication}</td>
                    <td className="px-2 py-2">{m.dosage || "—"}</td>
                    <td className="px-2 py-2">{m.batch_number || "—"}</td>
                    <td className="px-2 py-2">{m.expiry_date || "—"}</td>
                    <td className="px-2 py-2">{m.administered_by_name} ({ROLE_LABELS[m.administered_by_role] || m.administered_by_role})</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="mb-8">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8] mb-3">Clinical Notes ({notes.length})</h2>
          {notes.length === 0 ? (
            <p className="text-sm text-slate-500">No notes recorded.</p>
          ) : (
            <div className="space-y-2">
              {[...notes].reverse().map((n) => (
                <div key={n.id} className="border border-slate-200 rounded-lg px-4 py-3">
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{n.text}</p>
                  <p className="text-[11px] text-slate-500 mt-1">
                    {n.author_name} ({ROLE_LABELS[n.author_role] || n.author_role}) · {fmt(n.created_at)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mb-8">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8] mb-3">
            Refusal of Medical Treatment ({signatures.length})
          </h2>
          {signatures.length === 0 ? (
            <p className="text-sm text-slate-500">No refusal forms on record.</p>
          ) : (
            <div className="space-y-3">
              {[...signatures].reverse().map((s) => (
                <div key={s.id} className="border border-slate-200 rounded-lg px-4 py-3">
                  <p className="text-sm text-slate-800">
                    <span className="font-semibold">{s.patient_name}</span> refused medical treatment
                    {s.note && <> — {s.note}</>}
                  </p>
                  <img src={s.signature_data} alt={`Signature of ${s.patient_name}`} className="h-14 mt-2 border border-slate-100 rounded bg-white" />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Witnessed by {s.witnessed_by_name} ({ROLE_LABELS[s.witnessed_by_role] || s.witnessed_by_role}) · {fmt(s.created_at)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <footer className="border-t border-slate-200 pt-4 text-[11px] text-slate-400 flex justify-between">
          <span>Wales Event Medical Services · Professional Event Medical Cover UK</span>
          <span>Status: {patient.status === "discharged" ? "Discharged" : "Under care"}</span>
        </footer>
      </div>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="email-report-dialog">
          <DialogHeader>
            <DialogTitle>Email report to patient</DialogTitle>
            <DialogDescription>
              The full clinical report for {patient.first_name} {patient.last_name} will be emailed to the address below.
            </DialogDescription>
          </DialogHeader>
          <input
            type="email"
            data-testid="patient-report-email-input"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            placeholder="patient@example.com"
            className="w-full h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]"
          />
          <button
            onClick={sendEmail}
            disabled={sendingEmail}
            data-testid="patient-report-email-send"
            className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white font-semibold text-sm disabled:opacity-60"
          >
            <Mail className="h-4 w-4" /> {sendingEmail ? "Sending…" : "Send report"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
